import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { RestaurantProfile, LegalPagesSettings } from '../types';
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  Clock,
  ShieldCheck,
  Check,
  Sparkles,
  Flame,
  Globe,
  FileText,
  AlertCircle
} from 'lucide-react';

export const FooterSettingsStudio: React.FC = () => {
  const {
    restaurantProfile,
    saveRestaurantProfile,
    legalSettings,
    saveLegalSettings,
    currentView
  } = useApp();

  const [profile, setProfile] = useState<RestaurantProfile>(restaurantProfile);
  const [legal, setLegal] = useState<LegalPagesSettings>(legalSettings);

  const activeTab =
    currentView === 'staff-settings-privacy'
      ? 'privacy'
      : currentView === 'staff-settings-terms'
      ? 'terms'
      : currentView === 'staff-settings-allergens'
      ? 'allergens'
      : 'business';

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveRestaurantProfile(profile);
    await saveLegalSettings(legal);
    alert('Business and legal settings saved successfully!');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Dynamic Page Header based on Sidebar Selection */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          padding: '16px 20px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--accent-copper-light)',
              color: 'var(--accent-copper)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {activeTab === 'business' && <Building2 size={20} />}
            {activeTab === 'privacy' && <ShieldCheck size={20} />}
            {activeTab === 'terms' && <FileText size={20} />}
            {activeTab === 'allergens' && <AlertCircle size={20} />}
          </div>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 800 }}>
              {activeTab === 'business' && 'Restaurant Business Profile & Opening Hours'}
              {activeTab === 'privacy' && 'Finnish & EU GDPR Privacy Policy'}
              {activeTab === 'terms' && 'Customer Ordering & Terms of Service'}
              {activeTab === 'allergens' && 'Allergen & Finnish Food Safety (Oiva)'}
            </h2>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {activeTab === 'business' && 'Company address, VAT business ID, contact numbers and weekly hours'}
              {activeTab === 'privacy' && 'GDPR customer data processing statement visible in customer footer'}
              {activeTab === 'terms' && 'Cancellation, order modification, and payment terms'}
              {activeTab === 'allergens' && 'Finnish Food Act EU 1169/2011 notice and allergen guidelines'}
            </div>
          </div>
        </div>

        <button className="btn-primary" onClick={handleSave}>
          <Check size={16} />
          <span>Save Changes</span>
        </button>
      </div>

      {/* 1. RESTAURANT BUSINESS PROFILE */}
      {activeTab === 'business' && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Restaurant Brand Name *</label>
              <input
                type="text"
                className="form-input"
                value={profile.name}
                onChange={e => setProfile({ ...profile, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Finnish Business ID (Y-tunnus) *</label>
              <input
                type="text"
                className="form-input"
                value={profile.business_id}
                onChange={e => setProfile({ ...profile, business_id: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Website Domain</label>
              <input
                type="text"
                className="form-input"
                value={profile.domain}
                onChange={e => setProfile({ ...profile, domain: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Customer Contact Phone</label>
              <input
                type="text"
                className="form-input"
                value={profile.phone}
                onChange={e => setProfile({ ...profile, phone: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Official Email</label>
              <input
                type="email"
                className="form-input"
                value={profile.email}
                onChange={e => setProfile({ ...profile, email: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Street Address</label>
              <input
                type="text"
                className="form-input"
                value={profile.address}
                onChange={e => setProfile({ ...profile, address: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>City</label>
              <input
                type="text"
                className="form-input"
                value={profile.city}
                onChange={e => setProfile({ ...profile, city: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Postal Code</label>
              <input
                type="text"
                className="form-input"
                value={profile.postal_code}
                onChange={e => setProfile({ ...profile, postal_code: e.target.value })}
              />
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '18px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={16} color="var(--accent-copper)" />
              <span>Opening Hours Schedule</span>
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
              <div className="form-group">
                <label className="form-label" style={{ fontWeight: 800 }}>Opening Hours (English)</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={profile.opening_hours_en}
                  onChange={e => setProfile({ ...profile, opening_hours_en: e.target.value })}
                  placeholder="Mon–Thu: 10:30–21:00, Fri: 10:30–22:00, Sat: 12:00–22:00, Sun: 12:00–20:00"
                />
              </div>

              <div className="form-group">
                <label className="form-label" style={{ fontWeight: 800 }}>Aukioloajat (Finnish)</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={profile.opening_hours_fi}
                  onChange={e => setProfile({ ...profile, opening_hours_fi: e.target.value })}
                  placeholder="Ma–To: 10:30–21:00, Pe: 10:30–22:00, La: 12:00–22:00, Su: 12:00–20:00"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. GDPR PRIVACY POLICY */}
      {activeTab === 'privacy' && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>GDPR Policy Text (English)</label>
              <textarea
                className="form-input"
                rows={8}
                value={legal.privacy_policy_en}
                onChange={e => setLegal({ ...legal, privacy_policy_en: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>GDPR Policy Text (Finnish - Tietosuojaseloste)</label>
              <textarea
                className="form-input"
                rows={8}
                value={legal.privacy_policy_fi}
                onChange={e => setLegal({ ...legal, privacy_policy_fi: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {/* 3. TERMS OF SERVICE */}
      {activeTab === 'terms' && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Terms of Service (English)</label>
              <textarea
                className="form-input"
                rows={8}
                value={legal.terms_of_service_en}
                onChange={e => setLegal({ ...legal, terms_of_service_en: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Terms of Service (Finnish - Tilaus- ja Käyttöehdot)</label>
              <textarea
                className="form-input"
                rows={8}
                value={legal.terms_of_service_fi}
                onChange={e => setLegal({ ...legal, terms_of_service_fi: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {/* 4. ALLERGENS & OIVA FOOD SAFETY */}
      {activeTab === 'allergens' && (
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Allergen Policy & Food Safety Guidelines (English)</label>
              <textarea
                className="form-input"
                rows={6}
                value={legal.allergen_policy_en}
                onChange={e => setLegal({ ...legal, allergen_policy_en: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontWeight: 800 }}>Allergeenitiedot & Oiva-raportti (Finnish)</label>
              <textarea
                className="form-input"
                rows={6}
                value={legal.allergen_policy_fi}
                onChange={e => setLegal({ ...legal, allergen_policy_fi: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
